import { EMEARegion, NARegion } from "@dana/Common/Constants";
import { formatDateWithDash,formatDateRegion } from "@dana/Common/utils";
import InvoiceRecepients from "@dana/Components/OrderHistory/InvoiceRecepients";
import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useRef } from "react";
import { css } from "styled-components";
import { CommercialExcelDownloadButton } from "@dana/Components/ExcelDownloadButton/CommercialExcelDownloadButton";
import { NACommercialExcelDownloadButton } from "@dana/Components/ExcelDownloadButton/NACommercialExcelDownloadButton";

interface CommercialInvoiceProps {
    cominvoiceData: InvoiceDetailsResponse | null;
    websiteData: Website;
    selectedRegion: string | undefined;
}

export interface CommercialInvoiceStyles {
    invoiceContainer?: InjectableCss;
}

export const CommercialInvoiceStyles: CommercialInvoiceStyles = {
    invoiceContainer: {
        css: css`
            font-size: ${fontSizes.small};
            line-height: ${lineHeights.small};
            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }
            table {
                width: 100%;

                .price-cell {
                    text-align: right;
                }

                .align-right {
                    text-align: right;
                }

                .align-left {
                    text-align: left;
                }

                .align-center {
                    text-align: center;
                }

                .break-spaces {
                    white-space: break-spaces;
                }

                td {
                    vertical-align: top;
                }

          
                &.data-table {
                    border-collapse: collapse;
                    overflow-x: auto;
                    td {
                        padding: 4px;
                        border: 1px solid ${themeBackgrounds.lightGray};
                        text-align: left;
                        white-space: nowrap;
                    }

                    .section-title {
                        font-weight: ${fontWeights.semiBold};
                        text-transform: uppercase;
                        margin-bottom: 8px;
                    }

                    .spacer {
                        height: 115px;
                    }

                    .checkboxes {
                        &,
                        & > div {
                            display: flex;
                            align-items: center;
                        }
                        gap: 24px;

                        input {
                            width: 24px;
                            height: 24px;
                        }
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            th,
                            td {
                                white-space: normal;
                            }
                            &.data-table {
                                td {
                                    white-space: normal;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
                
            }

            td.company-logo {
                width: 200px;
            }

            td.reprint-text {
                width: 180px;
                text-align: right;
                vertical-align: top;
            }
            .address,
            address {
                font-style: normal;
            }

            .addresses-table {
                margin-bottom: 24px;
            }

            .company-address {
                h3 {
                    font-weight: ${fontWeights.semiBold};
                }
            }

            .pricing-box {
                > div {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin: 8px 0;
                }
            }

            .signature {
                max-width: 400px;
                margin-left: auto;
                text-align: center;
                padding: 30px 20px;

                span {
                    display: block;
                    border: 1px solid ${themeColors.primary.richBlack};
                    margin-bottom: 12px;
                }
            }
        `,
    },
};

const styles = CommercialInvoiceStyles;


const CommercialInvoice: FC<CommercialInvoiceProps> = ({ cominvoiceData, websiteData, selectedRegion }) => {
    const modalContentRef = useRef<HTMLDivElement>(null);
    const totalPrice = cominvoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.netAmount ?? 0), 0) ?? 0;
    const totalWeight = cominvoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.weight ?? 0), 0) ?? 0;
    const totalDiscount = cominvoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.discount ?? 0), 0) ?? 0;
    const miscCharges = cominvoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.miscCharges ?? 0), 0) ?? 0;
    const ifni = cominvoiceData?.invoiceInfo;
    const weighUnit = cominvoiceData?.invoiceInfo?.find(item => item.weightUnit)?.weightUnit ?? null;
    const totalTariff = (ifni ?? []).reduce((sum, inv) => {
        const parts = Array.isArray(inv.partPriceInfo) ? inv.partPriceInfo : [];
        const tariffSumForLine = parts
            .filter(
                p =>
                    String(p.priceType ?? "")
                        .trim()
                        .toLowerCase() === "tariff",
            )
            .reduce((s, p) => s + (Number(p.amount) || 0), 0);
        return sum + tariffSumForLine;
    }, 0);
    let totalCertiCost=0;
    if(selectedRegion===EMEARegion)
        {
        totalCertiCost = (ifni ?? []).reduce((sum, inv) => {           
            const parts = Array.isArray(inv.partPriceInfo) ? inv.partPriceInfo : [];    
            const certCostSumForLine = parts
                .filter(
                    p =>
                        String(p?.priceType ?? "")
                            .trim()
                            .toLowerCase() === "3.1 certification",
                )
                .reduce((s, p) => s + (Number(p?.amount) || 0), 0);
    
            return sum + certCostSumForLine;
        }, 0);
        }

    const totalAmt = (totalPrice + miscCharges + totalCertiCost + totalTariff) - totalDiscount;
    const message2 = siteMessage("CommercialInvoice_DeclareFooterStatement");
    const message1 = siteMessage("CommercialInvoice_FooterStatement");
    return (
        <StyledWrapper {...styles.invoiceContainer} ref={modalContentRef}>
            <div className="invoice-actions text-right">
                {selectedRegion===EMEARegion && (
                               <CommercialExcelDownloadButton
                                invoice={cominvoiceData}
                                totalAmt={totalAmt}
                                totalDiscount={totalDiscount}
                                totalCertiCost={totalCertiCost}
                                totalPrice={totalPrice}
                                weightUnit={weighUnit ??""}
                                totalWeight={totalWeight}
                                miscCharges={miscCharges}
                                fileName="emea-commercial-invoice"
                                sheetName="commercial-invoice"
                                selectedRegion={selectedRegion}              
                            />)}
                            {selectedRegion===NARegion && (
                               <NACommercialExcelDownloadButton
                                invoice={cominvoiceData}
                                totalAmt={totalAmt}
                                totalDiscount={totalDiscount}                               
                                totalPrice={totalPrice}
                                weightUnit={weighUnit ??""}
                                totalWeight={totalWeight}
                                miscCharges={miscCharges}
                                totalTariff={totalTariff}
                                fileName="na-commercial-invoice"
                                sheetName="commercial-invoice"
                                selectedRegion={selectedRegion}              
                            />)}
                {cominvoiceData !== null && (
                    <InvoiceRecepients
                        invoicetype="commercial"
                        invoiceData={cominvoiceData}
                        contentRef={modalContentRef}
                    />
                )}
                <PrintButton label={translate("Print Commercial Invoice")} targetRef={modalContentRef} />
            </div>

            <table className="data-table">
                <tbody>
                    <tr>
                        <td rowSpan={2} colSpan={2}>
                            <p className="section-title">{translate("Exporter")} </p>
                            <address>
                                {cominvoiceData?.shipPlantName}
                                {cominvoiceData?.shipPlantAddress1 && (
                                    <>
                                        <br />
                                        {cominvoiceData.shipPlantAddress1}
                                    </>
                                )}
                                {cominvoiceData?.shipPlantCountry && (
                                    <>
                                        <br />
                                        {cominvoiceData.shipPlantCountry}
                                    </>
                                )}
                            </address>
                        </td>
                        <td>
                            <p className="section-title">{translate("Invoice No.")}</p>
                            <p>{cominvoiceData?.invoiceNumber}</p>
                        </td>
                        <td>
                            <p className="section-title">{translate("Date")}</p>
                            <p>
                                {formatDateRegion(selectedRegion, cominvoiceData?.invoiceDate?.toString()?.split('T')[0] || "")}
                            </p>
                        </td>
                    </tr>
                    <tr>                      
                        <td>
                            {selectedRegion !== EMEARegion && (
                                <>
                            <p className="section-title">{translate("Purchase Order No.")}</p>
                            <p>{cominvoiceData?.poNumber}</p>
                                </>
                            )}
                        </td>
                         <td>
                            <p className="section-title">{translate("B/L, AWB NO.")}</p>
                            <p>{cominvoiceData?.deliveryNumber}</p>
                        </td>
                    </tr>
                    <tr>
                        <td rowSpan={2} colSpan={2}>
                            <p className="section-title">
                                {translate("Ultimate Consignee")} <span>{cominvoiceData?.shipTo}</span>
                            </p>
                            <address>
                                {cominvoiceData?.shipName}
                                {cominvoiceData?.shipAddress1 && (
                                    <>
                                        <br />
                                        {cominvoiceData.shipAddress1}
                                    </>
                                )}
                                {(
                                    cominvoiceData?.shipCity ||
                                    cominvoiceData?.shipState ||
                                    cominvoiceData?.shipZip
                                ) && (
                                        <>
                                            <br />
                                            {[
                                                cominvoiceData?.shipCity,
                                                cominvoiceData?.shipState,
                                                cominvoiceData?.shipZip,
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                        </>
                                    )}
                                {cominvoiceData?.shipCountry && (
                                    <>
                                        <br />
                                        {cominvoiceData.shipCountry}
                                    </>
                                )}
                            </address>
                        </td>
                        <td>
                            <p className="section-title">{translate("Country of Origin")}</p>
                            <p>{cominvoiceData?.invoiceInfo?.[0].countryOfOrigin}</p>
                        </td>
                        <td>
                            <p className="section-title">{translate("Date of Export")}</p>
                            <p>{formatDateRegion(selectedRegion,cominvoiceData?.documentDate?.split('T')[0] || "")}</p>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <p className="section-title">{translate("Letter of Credit")}</p>
                            <p>{cominvoiceData?.letterOfCredit}</p>
                        </td>
                        <td>
                            <p className="section-title">{translate("Incoterms")}</p>
                            <p>
                                {cominvoiceData?.incotermsCode} {cominvoiceData?.incotermsDescription}
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <td rowSpan={2} colSpan={2}>
                            <p className="section-title">
                                {translate("Purchasing Consignee")} <span>{cominvoiceData?.mailTo}</span>
                            </p>
                             <address>
                                {cominvoiceData?.mailName}
                                {cominvoiceData?.mailAddress1 && (
                                    <>
                                        <br />
                                        {cominvoiceData.mailAddress1}
                                    </>
                                )}
                                {(
                                    cominvoiceData?.mailCity ||
                                    cominvoiceData?.mailState ||
                                    cominvoiceData?.mailZip
                                ) && (
                                        <>
                                            <br />
                                            {[
                                                cominvoiceData?.mailCity,
                                                cominvoiceData?.mailState,
                                                cominvoiceData?.mailZip,
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                        </>
                                    )}
                                {cominvoiceData?.mailCountry && (
                                    <>
                                        <br />
                                        {cominvoiceData.mailCountry}
                                    </>
                                )}
                            </address>
                        </td>
                        <td colSpan={2}>
                            <p className="section-title">{translate("Terms of Payment")}</p>
                            <p>{cominvoiceData?.termsDescription}</p>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={2}>
                            <p className="section-title">{translate("REMARKS")}</p>
                            <p>{translate("CONTAINER: BOX")}</p>
                            <p>{translate("FEDERAL TAX ID")}: 26-1346950-01</p>
                        </td>
                    </tr>
                    <tr>
                        <td rowSpan={2} colSpan={2}>
                            <p className="section-title">{translate("Forwarding Agent/Broker")}</p>
                            <p>{cominvoiceData?.forwardingAgentAdd}</p>
                            <address>
                                {cominvoiceData?.forwardingAgentAddress1}
                                {(
                                    cominvoiceData?.forwardingAgentCity ||
                                    cominvoiceData?.forwardingAgentState ||
                                    cominvoiceData?.forwardingAgentZip
                                ) && (
                                    <>
                                        <br />
                                        {[
                                            cominvoiceData?.forwardingAgentCity,
                                            cominvoiceData?.forwardingAgentState,
                                            cominvoiceData?.forwardingAgentZip,
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    </>
                                )}
                                {cominvoiceData?.forwardingAgentCountry && (
                                    <>
                                        <br />
                                        {cominvoiceData.forwardingAgentCountry}
                                    </>
                                )}
                            </address>
                        </td>
                        <td rowSpan={2}>
                            <p className="section-title">{translate("Port of Export")}</p>
                            <p>{cominvoiceData?.portOfExport}</p>
                        </td>
                        <td rowSpan={2}>
                            <p className="section-title">{translate("Por of DEST.(Vessel, air only)")}</p>
                            <p>{cominvoiceData?.portOfDestination}</p>
                        </td>
                    </tr>
                    <tr>
                        <div className="spacer"></div>
                    </tr>
                    <tr>
                        <td>
                            <p className="section-title">{translate("Estimated Time Of Arrival")}</p>
                            <p>{formatDateRegion(selectedRegion,cominvoiceData?.estimatedTimeOfArrival?.split('T')[0] || "")}</p>
                        </td>
                        <td>
                            <p className="section-title">{translate("Containerized (Vessel Only)")}</p>
                            <div className="checkboxes">
                                <div>
                                    <input type="checkbox" name="Yes" id="Yes" />
                                    <label htmlFor="Yes">{translate("Yes")}</label>
                                </div>
                                <div>
                                    <input type="checkbox" name="No" id="No" />
                                    <label htmlFor="No">{translate("No")}</label>
                                </div>
                            </div>
                        </td>
                        <td>
                            <p className="section-title">{translate("Mode Of Transport")}</p>
                            <p>{cominvoiceData?.modeOfTransport}</p>
                        </td>
                        <td>
                            <p className="section-title">{translate("Exporting Carrier")}</p>
                            <p>{cominvoiceData?.exportingCarrier}</p>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Line Item Table */}
            <StyledTable>
                <thead>
                    <tr>
                        <th>{translate("Item")}</th>
                        <th>{translate("Description of Merchandise")}</th>
                        <th>{translate("H.S. Number")}</th>
                        <th>
                            {translate("Item Weight ")} ({weighUnit})
                        </th>
                        {selectedRegion !== EMEARegion && (
                            <th>{translate("Quantity")}</th>
                        )}
                        <th>
                            {translate("Unit Price In")} {cominvoiceData?.currency}
                        </th>
                         {(selectedRegion === EMEARegion) && (
                        <th>
                            {translate("3.1 Certificate Cost")} ({cominvoiceData?.currency})
                        </th>
                          )}
                        <th>
                            {translate("Value In")} {cominvoiceData?.currency}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {cominvoiceData?.invoiceInfo?.map((item, index) => (
                        <tr key={index}>
                            <td>{item.lineNumber}</td>
                            <td className="break-spaces">
                                <div>
                                    {item.partDescription}
                                    <br />
                                    {translate("Country of Origin")}: {item.countryOfOrigin}
                                    <br />
                                    {translate("Dana Part No")}: {item.partNumber}
                                    <br />
                                    {translate("Customer Part Number")}: {item.customerPartNumber}
                                    {selectedRegion===EMEARegion && (<>
                                    <br />
                                    {translate("Customer PO")}: {item.poNumber}
                                    <br />
                                    {translate("Order Number")}: {item.orderNumber}
                                    </>)}
                                </div>
                            </td>
                            <td>{item.hsNumber}</td>
                            <td className="price-cell">{selectedRegion === EMEARegion ? formatDecimal(item.weight, { locale: "fr-FR" }):item.weight}</td>
                            {selectedRegion !== EMEARegion && (
                                <td className="price-cell">
                                    {item.quantityOrdered} {item.unitOfMeasure}
                                </td>
                            )}                            
                            <td className="price-cell">{selectedRegion === EMEARegion ? formatDecimal(item.pricePerUnit, { locale: "fr-FR" }) : formatDecimal(item.pricePerUnit)}</td>
                              {(selectedRegion === EMEARegion) && (
                                 <td className="price-cell">
                                 {formatDecimal(item.partPriceInfo?.find(p => p.priceType==="3.1 Certification")?.amount ?? 0)}
                                    </td>
                              )}
                            <td className="price-cell">{selectedRegion === EMEARegion ?                             
                            formatDecimal((item.netAmount ?? 0) , { locale: "fr-FR" })
                             : formatDecimal(item.netAmount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={2}></td>
                        <td>
                            <strong>{translate("Total Weight")}</strong>
                        </td>
                        <td className="align-right">{selectedRegion === EMEARegion ? formatDecimal(totalWeight.toFixed(2), { locale: "fr-FR" }):totalWeight.toFixed(2)}{}</td>
                        <td colSpan={2}>
                            <strong>{translate("Line Total")}</strong>
                        </td>
                        <td className="align-right">{(selectedRegion === EMEARegion ? formatDecimal(totalPrice, { locale: "fr-FR" }) : formatDecimal(totalPrice))}</td>
                    </tr>
                    <tr>
                        <td colSpan={4}> 
                            {(selectedRegion === EMEARegion) && (
                            <StyledWrapper>
                                            <p>
                                                <strong>{translate("Remit To")}:</strong>
                                            </p>
                                            <address>
                                                {cominvoiceData?.remitToName}
                                                {cominvoiceData?.remitToAddress1 && (
                                                    <>
                                                        <br />
                                                        {cominvoiceData.remitToAddress1}
                                                    </>
                                                )}
                                                {(
                                                    cominvoiceData?.remitToCity                                                   
                                                ) && (
                                                    <>
                                                        <br />
                                                        {[
                                                            cominvoiceData?.remitToCity,                                                          
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                    </>
                                                )}      
                                                
                                            </address>
                                        </StyledWrapper> 
                                    )}
                                        </td>
                        <td colSpan={3} rowSpan={3}>
                            <div className="pricing-box">
                                 {(selectedRegion === EMEARegion) && (
                                <div>
                                      <strong>{translate("Total 3.1 Certificate Cost")})</strong>
                                      <p>{formatDecimal(totalCertiCost, { locale: "fr-FR" })} </p>
                                </div>
                                 )}
                                <div>
                                    <strong>{translate("MISC CHARGES (Packing, Freight, etc.)")}</strong>
                                    <p>{(selectedRegion === EMEARegion ? formatDecimal(miscCharges, { locale: "fr-FR" }) : formatDecimal(miscCharges))}</p>
                                </div>
                                {(selectedRegion !== EMEARegion) && (
                                    <div>
                                        <strong>{translate("Tariff Charges")}</strong>
                                        <p>{formatDecimal(totalTariff)}</p>
                                    </div>
                                )}
                                <div>
                                    <strong>{translate("Discount")}</strong>
                                    <p>{(selectedRegion === EMEARegion ? formatDecimal(totalDiscount, { locale: "fr-FR" }) :formatDecimal(totalDiscount))}</p>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr></tr>
                    <tr></tr>
                    <tr>
                        <td colSpan={4}></td>
                        <td colSpan={2}>
                            <strong>{translate("Invoice Total")}</strong>
                        </td>
                       <td className="align-right">{selectedRegion === EMEARegion ? formatDecimal(totalAmt, { locale: "fr-FR" }) : formatDecimal(totalAmt)}</td>
                    </tr>
                    <tr>
                        <td colSpan={7}>
                            <p>{message1}</p>
                        </td>
                    </tr>
                    <tr className="declaration-row">
                        <td colSpan={7}>
                            <p>
                                <strong>
                                    {message2}
                                    <br />
                                    {cominvoiceData?.shipPlantCity}, {cominvoiceData?.shipPlantState}
                                </strong>
                            </p>
                            <div className="signature">
                                <span></span>
                                {translate("AUTHORIZED SIGNATURE")}
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </StyledTable>
        </StyledWrapper>
    );
};

export default CommercialInvoice;
